import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../api/client';
import { Modal, Button, Spinner, Badge, Select, Input, useToast } from '../../components/ds';
import './FieldConfigModal.css';

const CATEGORIES = [
  { value: 'Content', label: 'Content — 文案內容' },
  { value: 'Medias', label: 'Medias — 素材媒體' },
  { value: 'URLs', label: 'URLs — 連結設定' },
  { value: 'Style', label: 'Style — 樣式主題' },
  { value: 'Trackings', label: 'Trackings — 追蹤像素' },
  { value: 'Advance', label: 'Advance — 進階設定' },
];

const TYPE_OVERRIDES = [
  { value: '', label: '（原始）' },
  { value: 'url', label: 'url — 連結輸入' },
  { value: 'color', label: 'color — 色票選擇器' },
  { value: 'textarea', label: 'textarea — 多行文字' },
];

export default function FieldConfigModal({ template, onClose }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const initialFieldsRef = useRef(null);
  const { addToast } = useToast();

  const fetchConfig = useCallback(async () => {
    try {
      const data = await api.getTemplateFieldConfig(template.uuid);
      const f = data.fields || [];
      setFields(f);
      initialFieldsRef.current = JSON.stringify(f);
    } catch (err) {
      addToast('載入欄位設定失敗：' + err.message);
    } finally {
      setLoading(false);
    }
  }, [template.uuid, addToast]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Group fields by component
  const grouped = useMemo(() => {
    const map = {};
    for (const f of fields) {
      const key = `${f.component_key}:${f.component_name}`;
      if (!map[key]) map[key] = { componentKey: f.component_key, componentName: f.component_name, fields: [] };
      map[key].fields.push(f);
    }
    return Object.values(map);
  }, [fields]);

  function updateField(idx, patch) {
    setFields((prev) => {
      const next = prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
      setDirty(JSON.stringify(next) !== initialFieldsRef.current);
      return next;
    });
  }

  function handleClose() {
    if (dirty && !window.confirm('尚未儲存，確定要離開嗎？')) return;
    onClose();
  }

  function getFieldIndex(componentKey, propertyKey) {
    return fields.findIndex(
      (f) => f.component_key === componentKey && f.property_key === propertyKey
    );
  }

  async function handleSave() {
    setSaving(true);
    try {
      // Only send fields that differ from defaults (have been configured)
      const toSave = fields
        .filter((f) => !f.locked)
        .map((f) => ({
          component_key: f.component_key,
          component_name: f.component_name,
          property_key: f.property_key,
          visible: f.visible,
          hidden_value: f.hidden_value || '',
          category: f.category === f.original_category ? '' : f.category,
          type_override: f.type_override || '',
          placeholder_size: f.placeholder_size || '',
        }));

      await api.saveTemplateFieldConfig(template.uuid, toSave);
      addToast('欄位設定已儲存', 'success');
      onClose();
    } catch (err) {
      addToast('儲存失敗：' + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={`欄位設定 — ${template.name}`}
      onClose={handleClose}
      size="lg"
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={handleClose}>取消</Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? '儲存中...' : '儲存設定'}
          </Button>
        </div>
      }
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
      ) : fields.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
          此 Template 沒有可設定的欄位（可能尚未快取 meta 資料，請先在 Portal 選擇此模板一次）
        </div>
      ) : (
        <div className="fc-groups">
          {grouped.map((group) => (
            <div key={`${group.componentKey}:${group.componentName}`} className="fc-group">
              <div className="fc-group-header">
                <span className="fc-comp-name">{group.componentName}</span>
                <Badge variant="neutral">{group.componentKey}</Badge>
              </div>
              <div className="fc-fields">
                {group.fields.map((field) => {
                  const idx = getFieldIndex(field.component_key, field.property_key);
                  return (
                    <FieldRow
                      key={`${field.component_key}.${field.property_key}`}
                      field={field}
                      onChange={(patch) => updateField(idx, patch)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function FieldRow({ field, onChange }) {
  if (field.locked) {
    return (
      <div className="fc-row fc-row-locked">
        <div className="fc-row-main">
          <span className="fc-lock-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </span>
          <span className="fc-prop-key">{field.property_key}</span>
          <span className="fc-locked-label">已鎖定（RD 設定）</span>
        </div>
        <div className="fc-row-meta">
          {field.description || field.label || ''} ({field.original_type})
          {field.locked_value && <> = <code>{field.locked_value}</code></>}
        </div>
      </div>
    );
  }

  return (
    <div className={`fc-row ${!field.visible ? 'fc-row-hidden' : ''}`}>
      <div className="fc-row-main">
        <label className="fc-checkbox">
          <input
            type="checkbox"
            checked={field.visible}
            onChange={(e) => onChange({
              visible: e.target.checked,
              hidden_value: e.target.checked ? '' : (field.hidden_value || defaultValueStr(field.default_value)),
            })}
          />
          <span className="fc-prop-key">{field.property_key}</span>
        </label>
        {!field.visible && (
          <span className="fc-hidden-badge">隱藏</span>
        )}
      </div>

      <div className="fc-row-meta">
        {field.description || field.label || ''} ({field.original_type})
      </div>

      {field.visible ? (
        <div className="fc-row-controls">
          <div className="fc-control">
            <label className="fc-control-label">Category</label>
            <select
              className="fc-select"
              value={field.category || field.original_category}
              onChange={(e) => onChange({ category: e.target.value })}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {field.original_type === 'string' ? (
            <div className="fc-control">
              <label className="fc-control-label">Type</label>
              <select
                className="fc-select"
                value={field.type_override || ''}
                onChange={(e) => onChange({ type_override: e.target.value })}
              >
                {TYPE_OVERRIDES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="fc-control">
              <label className="fc-control-label">Type</label>
              <span className="fc-type-fixed">{field.original_type}（不可改）</span>
            </div>
          )}

          {(field.original_type === 'image' || field.original_type === 'video' || field.original_type === 'medias') && (
            <div className="fc-control">
              <label className="fc-control-label">Placeholder 尺寸</label>
              <input
                className="fc-input fc-input-sm"
                type="text"
                value={field.placeholder_size || ''}
                onChange={(e) => onChange({ placeholder_size: e.target.value })}
                placeholder="960x450"
                style={{ width: 90 }}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="fc-row-controls">
          <div className="fc-control fc-control-wide">
            <label className="fc-control-label">固定值</label>
            {field.options && Array.isArray(field.options) && field.options.length > 0 ? (
              <select
                className="fc-select"
                value={field.hidden_value || ''}
                onChange={(e) => onChange({ hidden_value: e.target.value })}
              >
                <option value="">（請選擇）</option>
                {field.options.map((opt) => {
                  const val = typeof opt === 'object' ? opt.value : opt;
                  const label = typeof opt === 'object' ? opt.label : opt;
                  return <option key={val} value={val}>{label}</option>;
                })}
              </select>
            ) : (
              <input
                className="fc-input"
                type="text"
                value={field.hidden_value || ''}
                onChange={(e) => onChange({ hidden_value: e.target.value })}
                placeholder={defaultValueStr(field.default_value)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function defaultValueStr(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}
